import { FiArrowUpRight } from 'react-icons/fi';

export const phrasalVerbsData = {
  courseId: 'phrasal-verbs',
  courseType: 'vocabulary',
  title: 'Phrasal Verbs',
  icon: FiArrowUpRight,
  overview: {
    impact: 'Essential for natural English',
    focus: 'Verb + particle combinations',
    uses: 'Speaking and informal Writing',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'What Are Phrasal Verbs?',
      content: `<p>Phrasal verbs are combinations of verbs with particles (prepositions/adverbs) that create new meanings. They're common in everyday English and IELTS Speaking.</p>`,
      examples: [
        { label: 'Example', text: 'look up (= search for information)' },
        { label: 'Example', text: 'put off (= postpone)' },
        { label: 'Example', text: 'come across (= find by chance)' },
      ],
    },
    {
      title: 'Common IELTS Phrasal Verbs',
      content: `<p>These are useful for IELTS topics:</p>`,
      table: {
        headers: ['Phrasal Verb', 'Meaning', 'Example'],
        rows: [
          ['bring up', 'raise (a topic/child)', 'She brought up an interesting point.'],
          ['carry out', 'conduct, perform', 'They carried out research on the topic.'],
          ['come up with', 'think of (an idea)', 'Scientists came up with a solution.'],
          ['cut down on', 'reduce', 'We should cut down on plastic use.'],
          ['deal with', 'handle, manage', 'How can we deal with this problem?'],
        ],
      },
    },
    {
      title: 'Separable vs Inseparable',
      content: `<p>Some phrasal verbs can be split, others cannot:</p>`,
      points: [
        '<strong>Separable:</strong> "Turn the light off" OR "Turn off the light"',
        '<strong>Inseparable:</strong> "I came across this book" (NOT "came this book across")',
        'With pronouns, separable verbs MUST split: "Turn it off" (NOT "Turn off it")',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Meaning',
      type: 'select',
      instructions: 'Choose the meaning of each phrasal verb.',
      questions: [
        {
          text: '"Researchers looked into the effects of climate change."',
          options: ['investigated', 'saw', 'ignored'],
          answer: 'investigated',
          explanation: '"Look into" means to investigate or examine closely.',
        },
        {
          text: '"The meeting was put off until next week."',
          options: ['postponed', 'canceled', 'started'],
          answer: 'postponed',
          explanation: '"Put off" means to postpone or delay.',
        },
        {
          text: '"Technology has brought about major changes."',
          options: ['caused', 'prevented', 'described'],
          answer: 'caused',
          explanation: '"Bring about" means to cause something to happen.',
        },
        {
          text: '"We need to cut down on plastic waste."',
          options: ['reduce', 'increase', 'remove'],
          answer: 'reduce',
          explanation: '"Cut down on" means to reduce the amount of something.',
        },
        {
          text: '"Scientists came up with a new solution."',
          options: ['invented/thought of', 'rejected', 'forgot'],
          answer: 'invented/thought of',
          explanation: '"Come up with" means to think of or create an idea.',
        },
        {
          text: '"How do you deal with stress?"',
          options: ['handle/manage', 'cause', 'ignore'],
          answer: 'handle/manage',
          explanation: '"Deal with" means to handle or manage a situation.',
        },
      ],
    },
    {
      title: 'Complete the Phrasal Verb',
      type: 'fill-in',
      instructions: 'Type the missing particle (up, on, off, out, etc.).',
      questions: [
        {
          text: 'The government is trying to find ___ why pollution is increasing.',
          answer: 'out',
          explanation: '"Find out" means to discover or learn information.',
        },
        {
          text: 'We need to look ___ to our elders and respect their wisdom.',
          answer: 'up',
          explanation: '"Look up to" means to admire and respect someone.',
        },
        {
          text: 'They carried ___ extensive research on the topic.',
          answer: 'out',
          explanation: '"Carry out" means to conduct or perform (research, experiments).',
        },
        {
          text: 'Don\'t put ___ until tomorrow what you can do today.',
          answer: 'off',
          explanation: '"Put off" means to postpone or delay.',
        },
      ],
    },
    {
      title: 'Separable or Inseparable?',
      type: 'select',
      instructions: 'Can this phrasal verb be separated?',
      questions: [
        {
          text: '"Turn off the light" — Can we say "Turn the light off"?',
          options: ['Yes (Separable)', 'No (Inseparable)'],
          answer: 'Yes (Separable)',
          explanation: '"Turn off" is separable. Both forms are correct.',
        },
        {
          text: '"I came across an interesting article" — Can we say "came an article across"?',
          options: ['No (Inseparable)', 'Yes (Separable)'],
          answer: 'No (Inseparable)',
          explanation: '"Come across" cannot be separated.',
        },
        {
          text: '"She looked after her grandmother" — Can we say "looked her grandmother after"?',
          options: ['No (Inseparable)', 'Yes (Separable)'],
          answer: 'No (Inseparable)',
          explanation: '"Look after" is inseparable—the object must come after "after".',
        },
      ],
    },
    {
      title: 'Pronoun Placement',
      type: 'select',
      instructions: 'Which sentence is correct when using pronouns?',
      questions: [
        {
          text: 'For "turn off the TV"...',
          options: ['Turn it off', 'Turn off it'],
          answer: 'Turn it off',
          explanation: 'With pronouns, separable phrasal verbs MUST be split.',
        },
        {
          text: 'For "look after the children"...',
          options: ['Look after them', 'Look them after'],
          answer: 'Look after them',
          explanation: '"Look after" is inseparable, so the pronoun follows "after".',
        },
        {
          text: 'For "put away the dishes"...',
          options: ['Put them away', 'Put away them'],
          answer: 'Put them away',
          explanation: 'Separable verb + pronoun = verb + pronoun + particle.',
        },
      ],
    },
    {
      title: 'Use in Context',
      type: 'writing',
      instructions: 'Write sentences using these phrasal verbs for IELTS topics.',
      questions: [
        {
          text: 'Use "carry out" in a sentence about research.',
          example: 'Scientists have carried out extensive research on climate change.',
          tip: '"Carry out" is formal enough for IELTS Writing.',
        },
        {
          text: 'Use "come up with" in a sentence about solutions.',
          example: 'Governments need to come up with effective solutions to reduce pollution.',
          tip: 'This is commonly used in Speaking and semi-formal Writing.',
        },
        {
          text: 'Use "bring up" in a sentence about education.',
          example: 'The speaker brought up an interesting point about online education.',
          tip: '"Bring up" can mean to raise a topic OR to raise children.',
        },
      ],
    },
  ],
  tips: [
    'Learn phrasal verbs in context, not just definitions',
    'Use them naturally in Speaking for better fluency',
    'Be careful in formal Writing—some are too informal',
    'Practice with common IELTS topics',
  ],
  dailyPractice: [
    'Learn 3-5 new phrasal verbs daily',
    'Use them in speaking practice',
    'Watch TV shows and note phrasal verbs',
    'Create example sentences for each',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Phrasal Verbs: Multi-word verb expressions essential for natural English, particularly useful in IELTS Speaking.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...phrasalVerbsData,
      title: phrasalVerbsData.title + ' (Foundations)',
      overview: {
        ...phrasalVerbsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: phrasalVerbsData.sections.slice(0, Math.max(2, Math.ceil(phrasalVerbsData.sections.length / 2))),
      exercises: phrasalVerbsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...phrasalVerbsData,
      title: phrasalVerbsData.title + ' (Advanced)',
      overview: {
        ...phrasalVerbsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: phrasalVerbsData.sections,
      exercises: phrasalVerbsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(phrasalVerbsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return phrasalVerbsData;
}
